import React from 'react';

import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import Badge from '@adsk/alloy-react-badge';
import Divider from '@adsk/alloy-react-divider';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { useCostContext } from './CostProvider';
import { useTranslation } from './i18n';
import BlockRemoveButton from '../../../BlockRemoveButton';
import {
  getStatus,
  shouldShowStatus,
  MAPPING_KEY,
  BADGE_STATUS_COLOR_MAPPING,
} from '../helpers/consts';
import { getDeepLink } from '../helpers/formatEntities';
import { customizedType } from '../helpers/localeHelper';

const EntityBlock = ({
  entity,
  onRemoveClick,
  onReferenceClickAnalytics,
  type = '',
}) => {
  const { t } = useTranslation();
  const project = useCurrentProject();
  const { terminologies } = useCostContext();
  const status = getStatus(type, entity);
  const removeButtonClassName = 'EntityBlock__remove-button';
  const contentClassName = 'EntityBlock__content';
  const codeClassName = 'EntityBlock__code';

  const StyledA = styled(A)`
    display: flex;
    border-bottom: ${(p) =>
      p.isLast ? 'none' : `1px solid ${theme.colors.charcoal100};`};
    &:last-child {
      border-bottom: none;
    }
  `;

  const StyledEntityBlock = styled.div`
    width: 100%;
    display: flex;
    padding: 12px 16px;
    justify-content: space-between;
    align-items: center;
    color: ${theme.colors.charcoal900};
    &:hover .${removeButtonClassName} {
      display: block;
    }
    &:hover .${contentClassName} {
      width: calc(100% - 40px);
    }
    &:hover .${codeClassName} {
      max-width: 50%;
    }
  `;

  const StyledEntityBlockContent = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 4px;
  `;

  const StyledTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
  `;

  return (
    <StyledA
      onClick={onReferenceClickAnalytics}
      href={getDeepLink(entity, project, type)}
      target="__blank"
    >
      <StyledEntityBlock>
        <StyledEntityBlockContent className={contentClassName}>
          {shouldShowStatus(type) && (
            <div
              style={{
                padding: '4px 0',
              }}
            >
              {status && (
                <Badge
                  size={Badge.SIZES.SMALL}
                  variant={Badge.VARIANTS.LINE}
                  style={{ padding: 0 }}
                  color={BADGE_STATUS_COLOR_MAPPING[status]}
                >
                  {customizedType(t, status, terminologies)}
                </Badge>
              )}
            </div>
          )}
          <Tooltip
            getTargetProps={() => ({
              style: { width: '100%', display: 'flex', alignItems: 'center' },
            })}
            content={
              <StyledTooltipContent>
                <span>{entity[MAPPING_KEY[type]]}</span>
                <span>{entity.name}</span>
              </StyledTooltipContent>
            }
          >
            <div
              className={codeClassName}
              style={{ maxWidth: 'calc((100% - 40px)/2)' }}
            >
              <div style={theme.typography.ellipsis}>
                {entity[MAPPING_KEY[type]]}
              </div>
            </div>

            <Divider
              variant={Divider.VARIANTS.VERTICAL}
              height={12}
              color={theme.colors.charcoal400}
              style={{ margin: '0 12px', minWidth: '1px' }}
            />
            <div style={{ overflow: 'hidden' }}>
              <div style={theme.typography.ellipsis}>{entity.name}</div>
            </div>
          </Tooltip>
        </StyledEntityBlockContent>
        {onRemoveClick && (
          <BlockRemoveButton
            className={removeButtonClassName}
            style={{
              display: 'none',
              marginLeft: '16px',
            }}
            onClick={onRemoveClick}
          />
        )}
      </StyledEntityBlock>
    </StyledA>
  );
};

EntityBlock.displayName = 'EntityBlock';

export default EntityBlock;
